import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import UserAdminPage from './UserAdminPage';
import Sidebar from './Sidebar';
import TabsHeader from './TabsHeader';
import AuthContainer from './auth/AuthContainer';
import DocumentsView from './DocumentsView';
import AISettingsModal from './AISettingsModal';
import { FaEdit, FaSignOutAlt, FaCog, FaArrowDown, FaSpinner } from 'react-icons/fa';
import { authService, chatService } from '../services/api.service';
import { v4 as uuidv4 } from 'uuid';
import { API_URL } from '../config';


const AppContainer = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
`;

const ChatTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
`;

const BrandName = styled.span`
  color: #0084ff;
  font-weight: 700;
  margin-right: 5px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  margin-left: 10px;
  padding: 5px;
  
  &:hover {
    color: #0084ff;
  }
`;

const LogoutButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  gap: 5px;
  
  &:hover {
    color: #e53935;
  }
`;

const DocumentsButton = styled.button`
  padding: 8px 12px;
  background-color: #10a37f;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  color: white;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #0d8c6d;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 0;
  scroll-behavior: smooth;
  max-width: calc(90% - 350px);
  margin: 0 auto;
  width: 100%;
`;

const MessagesContainerChat = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 0;
  scroll-behavior: smooth;
  margin: 0 auto;
  width: 100%;
  
  /* Customize scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f0f0f0;
  }
  
  &::-webkit-scrollbar-thumb {
    background:rgb(123, 123, 123);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background:rgb(122, 123, 124);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  text-align: center;
  padding: 2rem;
`;

const EmptyStateTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const EmptyStateText = styled.p`
  font-size: 1rem;
  max-width: 500px;
`;

const ScrollToBottomButton = styled.button`
  position: fixed;
  bottom: 80px;
  right: 30px;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background-color: #0073e6;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
  opacity: 1;
  transition: opacity 0.2s, transform 0.2s;
  z-index: 1000;
  
  &:hover {
    opacity: 0.9;
    transform: scale(1.05);
  }
`;

const LoadingScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: #f5f5f5;
  color: #0084ff;
  font-size: 1.5rem;
  font-weight: 500;
`;

const Notification = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #4caf50;
  color: white;
  padding: 15px 20px;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 90%;
  width: auto;
  
  &.warning {
    background-color: #ff9800;
  }
  
  &.error {
    background-color: #f44336;
  }
`;

const LoadingMoreMessages = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px;
  color: #666;
  font-size: 14px;
  
  svg {
    margin-right: 8px;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Chat = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  const [activeChat, setActiveChat] = useState(null);
  const [chatSessions, setChatSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamedResponse, setStreamedResponse] = useState('');
  const [notification, setNotification] = useState(null);
  const [justRegistered, setJustRegistered] = useState(false);
  const [showAISettings, setShowAISettings] = useState(false);
  const [userAIPreferences, setUserAIPreferences] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(true); // Set showScrollButton to true initially for testing
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 50,
    total: 0,
    hasMore: false,
    isLoading: false
  });
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Check if user is already authenticated
  useEffect(() => {
    // Load user AI preferences when authenticated
    if (authenticated && user) {
      const loadUserPreferences = async () => {
        try {
          const response = await fetch(`${API_URL}/preferences/ai`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setUserAIPreferences(data);
          }
        } catch (error) {
          console.error('Failed to load AI preferences:', error);
        }
      };

      loadUserPreferences();
    }
  }, [authenticated, user]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authService.getCurrentUser();
        if (response.success) {
          setAuthenticated(true);
          setUser(response.user);
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Load user's chat sessions when authenticated
  useEffect(() => {
    if (authenticated && user) {
      loadUserSessions();
    }
  }, [authenticated, user]);

  // Load chat history when active chat changes
  useEffect(() => {
    if (activeChat) {
      // Reset pagination when changing chats
      setPagination({
        offset: 0,
        limit: 50,
        total: 0,
        hasMore: false,
        isLoading: false
      });
      setMessages([]); // Clear messages before loading new chat
      loadChatHistory(activeChat);
    }
  }, [activeChat]);

  // Scroll to bottom when messages change or when switching to chat tab
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamedResponse, activeTab]);

  // Handle scroll event to detect when user scrolls to top
  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      
      // Show/hide scroll to bottom button
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setShowScrollButton(distanceFromBottom > 100);
      
      // Load more messages when user scrolls near the top
      if (scrollTop < 50 && pagination.hasMore && !pagination.isLoading && activeChat) {
        loadMoreMessages();
      }
    }
  }, [pagination.hasMore, pagination.isLoading, activeChat]);

  // Add scroll event listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const loadUserSessions = async () => {
    try {
      const response = await chatService.getUserSessions();
      if (response.success) {
        setChatSessions(response.sessions);

        // Set active chat to the most recent session if available
        if (response.sessions.length > 0 && !activeChat) {
          setActiveChat(response.sessions[0].session_id);
        }
      }
    } catch (error) {
      console.error('Failed to load user sessions:', error);
    }
  };

  const loadChatHistory = async (sessionId) => {
    try {
      setPagination(prev => ({ ...prev, isLoading: true }));
      
      // Save current scroll position and height before loading more messages
      const container = messagesContainerRef.current;
      const scrollHeight = container ? container.scrollHeight : 0;
      const scrollTop = container ? container.scrollTop : 0;
      
      const response = await chatService.getChatHistory(sessionId, pagination.limit, pagination.offset);
      if (response.success) {
        // Convert history to message format
        const formattedMessages = response.history.map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.message || msg.content,
          timestamp: msg.created_at,
          blocked: msg.is_blocked === 1 || msg.is_blocked === true,
          violatedDocument: msg.violated_document_info ? JSON.parse(msg.violated_document_info) : null,
          documentId: msg.document_id,
          isNewMessage: false // Explicitly mark history messages as not new
        }));

        console.log('Loaded chat history:', formattedMessages);
        
        // If this is the initial load, replace messages
        // If this is a pagination load, append to existing messages
        if (pagination.offset === 0) {
          setMessages(formattedMessages);
          
          // Scroll to bottom on initial load
          setTimeout(() => {
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
            }
          }, 100);
        } else {
          setMessages(prev => [...formattedMessages, ...prev]);
          
          // Restore scroll position after loading more messages
          setTimeout(() => {
            if (container) {
              const newScrollHeight = container.scrollHeight;
              const heightDifference = newScrollHeight - scrollHeight;
              container.scrollTop = scrollTop + heightDifference;
            }
          }, 100);
        }
        
        setPagination(prev => ({ 
          ...prev, 
          total: response.pagination.total, 
          hasMore: response.pagination.hasMore,
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
      setPagination(prev => ({ ...prev, isLoading: false }));
    }
  };

  const loadMoreMessages = () => {
    if (pagination.hasMore && !pagination.isLoading && activeChat) {
      const newOffset = pagination.offset + pagination.limit;
      setPagination(prev => ({ ...prev, offset: newOffset }));
      loadChatHistory(activeChat);
    }
  };

  const handleSendMessage = async (message, documentId) => {
    // Create a new session if none is active
    let currentSessionId = activeChat;
    if (!currentSessionId) {
      currentSessionId = `session_${uuidv4()}`;
      setActiveChat(currentSessionId);
    }

    console.log('Sending message with params:', { message, documentId, currentSessionId });

    // Add user message to UI immediately
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message || 'Uploaded a document',
      timestamp: new Date().toISOString(),
      documentId: documentId // Store document ID with the message
    };
    setMessages(prevMessages => [...prevMessages, userMessage]);

    // Show typing indicator
    setIsLoading(true);

    try {
      // Send message to API
      const response = await chatService.sendMessage(message, currentSessionId, documentId);

      if (response.success) {
        // Add assistant response to messages
        const assistantMessage = {
          id: response.metadata.message_id,
          role: 'assistant',
          content: response.response,
          timestamp: response.metadata.timestamp,
          isNewMessage: true // Mark this as a new message for animation
        };

        setMessages(prevMessages => [...prevMessages, assistantMessage]);
      } else {
        // Check if this is a policy violation or AI usage rule violation
        if (response.error === 'Message blocked by AI usage rules') {
          // Check if this is a silent block (e.g., network information)
          if (response.silentBlock) {
            // For network information violations, don't add a message to the chat
            console.log('Message was silently blocked due to network information violation');
            // Remove the user's message from the chat
            setMessages(prevMessages => prevMessages.filter(msg => msg.id !== userMessage.id));
          } else {
            // Handle AI usage rule violation with document reference
            const blockedMessage = {
              id: Date.now(),
              role: 'assistant',
              content: `This message was blocked by AI usage rules: ${response.reason || ''}`,
              timestamp: new Date().toISOString(),
              is_blocked: true,
              // Include the violated document information if available
              violatedDocument: response.violatedDocument || null
            };

            setMessages(prevMessages => [...prevMessages, blockedMessage]);
          }
        } else {
          // Handle content policy violation
          console.log('Message blocked by content policy:', response);
          
          // Update the user message to show it was blocked
          setMessages(prevMessages => 
            prevMessages.map(msg => 
              msg.id === userMessage.id 
                ? {...msg, blocked: true} 
                : msg
            )
          );
          
          // Add the assistant response about the block
          const blockedMessage = {
            id: Date.now(),
            role: 'assistant',
            content: response.message || 'This message was blocked by our content policy.',
            timestamp: new Date().toISOString(),
            blocked: true,
            violatedDocument: response.violatedDocument || {
              reason: response.reason
            }
          };

          setMessages(prevMessages => [...prevMessages, blockedMessage]);
        }
      }

      // Refresh sessions list to show the new session
      loadUserSessions();
    } catch (error) {
      console.error('Failed to send message:', error);

      // Check if this is a rule violation error from the API
      if (error.response && error.response.data && error.response.data.error === 'Message blocked by AI usage rules') {
        // Check if this is a silent block (e.g., network information)
        if (error.response.data.silentBlock) {
          // For network information violations, don't add a message to the chat
          console.log('Message was silently blocked due to network information violation');
          // Remove the user's message from the chat
          setMessages(prevMessages => prevMessages.filter(msg => msg.id !== userMessage.id));
        } else {
          // Handle AI usage rule violation from error response
          const blockedMessage = {
            id: Date.now(),
            role: 'assistant',
            content: `This message was blocked by AI usage rules: ${error.response.data.reason || ''}`,
            timestamp: new Date().toISOString(),
            is_blocked: true,
            // Include the violated document information if available
            violatedDocument: error.response.data.violatedDocument || null
          };

          setMessages(prevMessages => [...prevMessages, blockedMessage]);
        }
      } else {
        // Show generic error message
        const errorMessage = {
          id: Date.now(),
          role: 'assistant',
          content: 'Sorry, there was an error processing your message. Please try again.',
          timestamp: new Date().toISOString(),
          is_error: true
        };

        setMessages(prevMessages => [...prevMessages, errorMessage]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthenticated = (userData, isNewRegistration = false) => {
    setAuthenticated(true);
    setUser(userData);

    // If this is a new registration, set the flag to show verification notification
    if (isNewRegistration) {
      setJustRegistered(true);
      setNotification({
        type: 'warning',
        message: 'Please check your email to verify and activate your account. You may have limited access until your account is verified.'
      });

      // Auto-dismiss notification after 10 seconds
      setTimeout(() => {
        setNotification(null);
      }, 10000);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setAuthenticated(false);
      setUser(null);
      setActiveChat(null);
      setChatSessions([]);
      setMessages([]);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNewChat = () => {
    setActiveChat(null);
    setMessages([]);
  };

  const handleSelectChat = (sessionId) => {
    setActiveChat(sessionId);
  };

  const handleDeleteChat = async (sessionId) => {
    try {
      await chatService.deleteSession(sessionId);

      // Update sessions list
      setChatSessions(prevSessions =>
        prevSessions.filter(session => session.session_id !== sessionId)
      );

      // If the deleted session was active, clear it
      if (activeChat === sessionId) {
        setActiveChat(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to delete chat session:', error);
    }
  };



  // Show loading screen while checking authentication
  if (authLoading) {
    return <LoadingScreen>Loading...</LoadingScreen>;
  }
  // If not authenticated, show auth container
  if (!authenticated) {
    return <AuthContainer onAuthenticated={(userData, isNewRegistration) => handleAuthenticated(userData, isNewRegistration)} />;
  }

  return (
    <AppContainer>
      {notification && (
        <Notification className={notification.type}>
          {notification.message}
        </Notification>
      )}
      <Sidebar
        activeChat={activeChat}
        sessions={chatSessions}
        onNewChat={handleNewChat}
        onSelectChat={handleSelectChat}
        onDeleteChat={handleDeleteChat}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onLogout={handleLogout}
      />

      <MainContent>
        <TabsHeader
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab);
            // If switching to chat tab, scroll to bottom after a short delay
            if (tab === 'chat' && messagesEndRef.current) {
              setTimeout(() => {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }
          }}
          userRole={user?.role}
        />


        {/* Render content based on selected tab */}
        {activeTab === 'chat' && (
          <ChatContainer>
            <ChatHeader>
              <ChatTitle>
                <BrandName>vcurd</BrandName>
                {/* {activeChat ? 'Chat Session' : 'New Chat'} */}
              </ChatTitle>
              <HeaderActions>
                {user?.role === 'admin' && (
                  <DocumentsButton onClick={() => setShowAISettings(true)}>
                    <FaCog size={14} style={{ marginRight: '5px' }} />
                    AI Settings
                  </DocumentsButton>
                )}
              </HeaderActions>
            </ChatHeader>

            <MessagesContainerChat ref={messagesContainerRef}>
              <MessagesContainer>
                {messages.length === 0 ? (
                  <EmptyState>
                    <EmptyStateTitle>Welcome to AI-Governance!</EmptyStateTitle>
                    <EmptyStateText>
                      Start a conversation with our intelligent assistant. Ask questions, get code examples, or discuss any topic - with built-in policy filtering for safe interactions.
                    </EmptyStateText>
                  </EmptyState>
                ) : (
                  <>
                    {pagination.isLoading && pagination.offset > 0 && (
                      <LoadingMoreMessages>
                        <FaSpinner size={16} />
                        Loading previous messages...
                      </LoadingMoreMessages>
                    )}
                    {messages.map((message) => (
                      <ChatMessage 
                        key={message.id} 
                        id={message.id}
                        role={message.role}
                        content={message.content}
                        timestamp={message.timestamp}
                        isNewMessage={message.isNewMessage}
                        messageIsBlocked={message.messageIsBlocked}
                        is_blocked={message.blocked}
                        isBlocked={message.blocked}
                        violatedDocument={message.violatedDocument}
                        documentId={message.documentId}
                      />
                    ))}
                    {isLoading && (
                      <ChatMessage
                        role="assistant"
                        content=""
                        timestamp={new Date().toISOString()}
                        isLoading={true}
                      />
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </MessagesContainer>
              {showScrollButton && (
                <ScrollToBottomButton onClick={() => messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })}>
                  <FaArrowDown size={20} />
                </ScrollToBottomButton>
              )}
            </MessagesContainerChat>

            <ChatInput 
              onSendMessage={handleSendMessage} 
              disabled={isLoading} 
              activeChat={activeChat} 
            />
          </ChatContainer>
        )}
        {activeTab === 'documents' && (
          <DocumentsView />
        )}

        {activeTab === 'admin' && (
          <UserAdminPage />
        )}
      </MainContent>

      {/* AI Settings Modal */}
      <AISettingsModal
        isOpen={showAISettings}
        onClose={() => setShowAISettings(false)}
        onSaved={(newPreferences) => {
          setUserAIPreferences(newPreferences);
          setNotification({
            type: 'success',
            message: 'AI model settings updated successfully!'
          });

          // Auto-dismiss notification after 5 seconds
          setTimeout(() => {
            setNotification(null);
          }, 5000);
        }}
      />
    </AppContainer>
  );
};

export default Chat;
